import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import { ConfidenceBadge } from "@/components/ConfidenceBadge";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Users, DollarSign, Home, Receipt, GraduationCap, ShieldAlert, ArrowRight, Loader2, Clock } from "lucide-react";

const COLORS = ["#F5A623", "#C8102E", "#C5A059", "#4C9AFF", "#0284C7", "#059669", "#7C3AED"];

function KPI({ icon: Icon, label, value, sub, testId }) {
  return (
    <Card className="p-5 bg-white exec-shadow" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="h-8 w-8 rounded-lg bg-slate-900/5 flex items-center justify-center"><Icon className="h-4 w-4 text-slate-900" /></div>
      </div>
      <div className="font-mono font-bold text-2xl mt-2 text-slate-900 tabular">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

export default function Portfolio() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/portfolio").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading portfolio…</div>;

  const countryData = Object.entries(data.by_country).map(([name, value]) => ({ name, value: Math.round(value) }));
  const gradeData = Object.entries(data.by_grade).map(([name, value]) => ({ name, value: Math.round(value) }));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto animate-fade-up" data-testid="portfolio-dash-container">
      <div className="text-xs uppercase tracking-[0.2em] text-[#B45309] font-semibold">Global Mobility</div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Portfolio Dashboard</h1>
      <p className="text-muted-foreground mt-2">Enterprise-wide assignment cost, exposure and data-quality intelligence.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KPI testId="portfolio-kpi-assignees" icon={Users} label="Total Assignees" value={data.total_assignees} sub={`${data.total_calculations} calculations`} />
        <KPI testId="portfolio-kpi-cost" icon={DollarSign} label="Total Annual Cost" value={fmt(data.total_annual_cost_usd, "USD")} sub="Employer cost (USD)" />
        <KPI icon={Home} label="Housing Exposure" value={fmt(data.housing_exposure_usd, "USD")} />
        <KPI icon={Receipt} label="Tax & Gross-Up" value={fmt(data.tax_cost_usd, "USD")} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <KPI icon={GraduationCap} label="Education Cost" value={fmt(data.education_cost_usd, "USD")} />
        <KPI icon={DollarSign} label="COLA Exposure" value={fmt(data.cola_exposure_usd, "USD")} sub="Net (may be negative)" />
        <KPI icon={ShieldAlert} label="Data-Quality Alerts" value={data.data_quality_alerts.length} sub="Low/insufficient confidence" />
        <KPI icon={Clock} label="Active Assignments" value={data.expiry_timeline.length} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <Card className="p-5 bg-white">
          <h3 className="font-display font-bold text-slate-900 mb-4">Cost by Host Country (USD)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={countryData} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => (v / 1000).toFixed(0) + "k"} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
              <Tooltip formatter={(v) => fmt(v, "USD")} />
              <Bar dataKey="value" fill="#F5A623" radius={[0, 4, 4, 0]}>
                {countryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5 bg-white">
          <h3 className="font-display font-bold text-slate-900 mb-4">Cost by Grade (USD)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={gradeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                {gradeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmt(v, "USD")} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {data.data_quality_alerts.length > 0 && (
        <Card className="mt-6 p-5 bg-amber-50 border-amber-200" data-testid="portfolio-alerts">
          <div className="flex items-center gap-2 text-amber-900 font-semibold mb-2"><ShieldAlert className="h-5 w-5" /> Data-Quality & Stale-Source Alerts</div>
          <div className="space-y-1 text-sm text-amber-800">
            {data.data_quality_alerts.map((a, i) => (
              <div key={i}>• {a.employee} → {a.host}: <span className="font-semibold uppercase">{a.confidence}</span> confidence — review required before issuance.</div>
            ))}
          </div>
        </Card>
      )}

      <Card className="mt-6 bg-white overflow-hidden" data-testid="portfolio-assignments-table">
        <div className="px-5 py-4 border-b"><h3 className="font-display font-bold text-slate-900">Assignments — drill down</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold uppercase text-xs tracking-wide">Employee</th>
                <th className="text-left px-4 py-2.5 font-semibold uppercase text-xs tracking-wide">Route</th>
                <th className="text-right px-4 py-2.5 font-semibold uppercase text-xs tracking-wide">Employer Cost</th>
                <th className="text-left px-4 py-2.5 font-semibold uppercase text-xs tracking-wide">Status</th>
                <th className="text-left px-4 py-2.5 font-semibold uppercase text-xs tracking-wide">Confidence</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {data.assignments.map((a) => (
                <tr key={a.calculation_id} className="border-t hover:bg-slate-100">
                  <td className="px-4 py-3 font-medium text-slate-900">{a.employee}</td>
                  <td className="px-4 py-3 text-slate-400">{a.route}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{fmt(a.cost_usd, "USD")}</td>
                  <td className="px-4 py-3">{a.status}</td>
                  <td className="px-4 py-3"><ConfidenceBadge level={a.confidence} /></td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/assignment/${a.calculation_id}`} data-testid={`portfolio-drilldown-${a.calculation_id}`} className="inline-flex items-center gap-1 text-essred font-semibold hover:underline">
                      Open <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
